import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import { psiTokens } from '../theme/psiTheme';
import { HeaderCell } from './ClassRecord';

// Rendered both by CertificatesPage.jsx (one instance per registered type,
// all on the shared /certificates URL) and by DynamicRecordPage.jsx (one
// instance alone, on its own direct URL like /GreenBeltCertificate).
// Because of that dual use, this component deliberately does NOT call
// usePageMeta itself — with multiple instances on /certificates, whichever
// rendered last would clobber the page title. Each caller sets its own
// meta instead: CertificatesPage.jsx sets one page-level title/description
// for the whole listing, DynamicRecordPage.jsx sets one for the single
// type it resolved. Public, unauthenticated page: `holders` must never
// carry a `name` field, see "Public page privacy" in CLAUDE.md.
export function CertificateTypeContent({ type, holders }) {
  return (
    <Box sx={{ backgroundColor: psiTokens.background.primary }}>
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
        {/* Page header — just the program name, in the certificate accent
            color. No "Certification" wording: the banner/table below
            already makes clear what this list is, per "Diplomas vs
            Certificates" in CLAUDE.md. */}
        <Typography variant="h2" sx={{ mb: 1.5, color: psiTokens.credential.certificate, fontWeight: 800 }}>
          {type.programName}
        </Typography>

        <Typography sx={{ fontSize: '14px', color: psiTokens.text.tertiary, fontWeight: 400, mb: 2 }}>
          {holders.length} certified &nbsp;·&nbsp; Program: <Box component="span" sx={{ fontWeight: 600, color: psiTokens.text.secondary }}>{type.programName}</Box>
        </Typography>

        <Box
          sx={{
            width: '48px',
            height: '3px',
            background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
            borderRadius: '2px',
            my: 2.5,
          }}
        />

        <Typography sx={{ fontSize: '14px', color: psiTokens.text.tertiary, lineHeight: 1.8, maxWidth: '720px', mb: 5 }}>
          {type.description}
        </Typography>

        {holders.length === 0 ? (
          <Typography sx={{ fontSize: '14px', color: psiTokens.text.tertiary }}>
            No certificates have been issued for this program yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto', border: `1px solid ${psiTokens.border}`, borderRadius: '10px' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <Box component="thead">
                <Box component="tr" sx={{ backgroundColor: psiTokens.background.tertiary }}>
                  <HeaderCell align="left">Record ID</HeaderCell>
                  <HeaderCell>Attendance</HeaderCell>
                  <HeaderCell align="left">Training Class</HeaderCell>
                  <HeaderCell align="left">Certification Project</HeaderCell>
                  <HeaderCell align="right">Verify</HeaderCell>
                </Box>
              </Box>
              <Box component="tbody">
                {holders.map((holder) => (
                  <Box
                    component="tr"
                    key={holder.recordId}
                    sx={{
                      borderBottom: `1px solid ${psiTokens.border}`,
                      '&:hover': { backgroundColor: psiTokens.background.tertiary },
                      '&:last-of-type': { borderBottom: 'none' },
                    }}
                  >
                    <Box component="td" sx={{ p: '12px 14px', fontWeight: 700, color: psiTokens.text.primary, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {holder.recordId}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        p: '12px 14px',
                        textAlign: 'center',
                        fontWeight: 600,
                        color: parseInt(holder.pct) >= 60 ? psiTokens.success : psiTokens.error,
                      }}
                    >
                      {holder.pct} ({holder.hrs} hrs)
                    </Box>
                    <Box component="td" sx={{ p: '12px 14px' }}>
                      <Typography
                        component={RouterLink}
                        to={`/${holder.classId}`}
                        sx={{ fontSize: '13px', color: psiTokens.primary.dark, textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { color: psiTokens.primary.light } }}
                      >
                        {holder.classId}
                      </Typography>
                    </Box>
                    <Box component="td" sx={{ p: '12px 14px', minWidth: '260px' }}>
                      <Typography sx={{ fontSize: '13px', color: psiTokens.text.secondary, lineHeight: 1.5 }}>
                        {holder.certificationProject || 'N/A'}
                      </Typography>
                    </Box>
                    <Box component="td" sx={{ p: '12px 14px', textAlign: 'right' }}>
                      <Button
                        component={RouterLink}
                        to={`/verify/${holder.recordId}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: psiTokens.credential.certificate,
                          color: psiTokens.credential.certificate,
                          fontSize: '11px',
                          fontWeight: 700,
                        }}
                      >
                        Verify
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}
